import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, ErrorState, Field, Input, ReadOnlyBanner, Textarea } from '@bb/ui';
import { ApiError, type AdminApi, type TemplateChannel, type TemplateContent, type TemplateDetails, type TemplateIssue } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { CardContentFields } from '../canvas/editor/steps/MessageStepPanel.tsx';
import { PushComposer } from '../composer/push/PushComposer.tsx';
import { detectMode, type PushComposerMode, type PushComposerValue } from '../composer/push/pushDraft.ts';
import { WebhookComposer } from '../composer/webhook/WebhookComposer.tsx';
import { TagPicker } from '../tags/TagPicker.tsx';
import { emptyTemplateContent, TEMPLATE_CHANNEL_LABELS, templateSlots } from './templateContent.ts';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
const issuesOf = (error: unknown): TemplateIssue[] => (error instanceof ApiError && Array.isArray(error.problem.issues) ? (error.problem.issues as TemplateIssue[]) : []);
const useApi = () => (useRouter().options.context as { api: AdminApi }).api;

/** Editors without a visual composer yet keep the slot as JSON (in-app BB-85/BB-90, banners BB-71, Canvas BB-110). */
function JsonSlotEditor({ label, value, readOnly, onChange }: { label: string; value: unknown; readOnly: boolean; onChange: (next: Record<string, unknown>) => void }) {
  const [text, setText] = useState(() => JSON.stringify(value ?? {}, null, 2));
  const [error, setError] = useState<string | null>(null);
  const edit = (next: string) => {
    setText(next);
    try {
      const parsed: unknown = JSON.parse(next);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) throw new Error('an object is expected');
      setError(null);
      onChange(parsed as Record<string, unknown>);
    } catch (e) {
      setError(`Not valid JSON: ${(e as Error).message}`);
    }
  };
  return (
    <Field label={label} hint="The visual editor for this channel comes with its composer ticket; the JSON is checked by the same validator on save." error={error ?? undefined}>
      <Textarea value={text} rows={12} spellCheck={false} disabled={readOnly} onChange={(e) => edit(e.target.value)} />
    </Field>
  );
}

/** The channel's composer over a template content: push, Content Card and webhook use the campaign composers. */
export function TemplateContentEditor({ environmentId, channel, content, readOnly, onChange }: { environmentId: string; channel: TemplateChannel; content: TemplateContent; readOnly: boolean; onChange: (next: TemplateContent) => void }) {
  const [mode, setMode] = useState<PushComposerMode>(() => (channel === 'push' ? detectMode(content as PushComposerValue) : 'shared'));
  if (channel === 'push')
    return <PushComposer environmentId={environmentId} category="marketing" mode={mode} onModeChange={setMode} value={content as PushComposerValue} onChange={(next) => onChange(next as TemplateContent)} readOnly={readOnly} />;
  if (channel === 'card')
    return <CardContentFields environmentId={environmentId} content={content.content_card ?? {}} readOnly={readOnly} launched={false} durationLabel="A set time after the card is created" onChange={(next) => onChange({ content_card: next })} />;
  if (channel === 'webhook') return <WebhookComposer environmentId={environmentId} value={content.webhook ?? {}} readOnly={readOnly} onChange={(next) => onChange({ webhook: next })} />;
  const slot = templateSlots(channel)[0]!;
  return <JsonSlotEditor label={`${TEMPLATE_CHANNEL_LABELS[channel]} content (${slot})`} value={content[slot]} readOnly={readOnly} onChange={(next) => onChange({ [slot]: next })} />;
}

function IssueList({ error }: { error: unknown }) {
  const issues = issuesOf(error);
  return (
    <div role="alert" className="bb-field__error">
      <p>{error instanceof ApiError && error.code === 'template_version_conflict' ? 'Someone saved a newer version meanwhile: reload the template before saving.' : errorText(error)}</p>
      {issues.length > 0 && (
        <ul aria-label="Content issues">
          {issues.map((i) => (
            <li key={`${i.path}-${i.code}`}>
              {i.path ? `${i.path}: ` : ''}
              {i.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A new template of one channel: name, description and content; saved as version 1. */
export function NewTemplateEditor({ environmentId, channel }: { environmentId: string; channel: TemplateChannel }) {
  const api = useApi();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState<TemplateContent>(() => emptyTemplateContent(channel));
  const create = useMutation({
    mutationFn: () => api.createTemplate(environmentId, { channel, name: name.trim(), content, ...(description.trim() ? { description: description.trim() } : {}) }),
    onSuccess: (created) => void navigate({ to: '/env/$environmentId/content/templates/$templateId', params: { environmentId, templateId: created.id } }),
  });
  return (
    <section className="template-editor">
      <p>
        <Link to="/env/$environmentId/content/templates" params={{ environmentId }}>
          ← Templates
        </Link>
      </p>
      <h1>New {TEMPLATE_CHANNEL_LABELS[channel].toLowerCase()} template</h1>
      <Field label="Template name">
        <Input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Description">
        <Textarea value={description} rows={2} maxLength={1000} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <TemplateContentEditor environmentId={environmentId} channel={channel} content={content} readOnly={false} onChange={setContent} />
      {create.isError && <IssueList error={create.error} />}
      <Button variant="primary" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>
        Create template
      </Button>
    </section>
  );
}

/** Details of a template being edited: the draft content starts from the current version. */
function EditorBody({ environmentId, template }: { environmentId: string; template: TemplateDetails }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { has } = usePermissions();
  const canEdit = has('content.edit') && !template.archivedAt;
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description ?? '');
  const [content, setContent] = useState<TemplateContent>(template.current.content);
  const [base, setBase] = useState(template.currentVersion);
  const [editorKey, setEditorKey] = useState(0);
  const [notes, setNotes] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const refresh = (next: TemplateDetails) => queryClient.setQueryData(['template', environmentId, template.id], next);
  const saveDetails = useMutation({
    mutationFn: () => api.updateTemplate(environmentId, template.id, { name: name.trim(), description: description.trim() || null }),
    onSuccess: (next) => {
      refresh(next);
      setNotice('Details saved.');
    },
  });
  const saveVersion = useMutation({
    mutationFn: () => api.addTemplateVersion(environmentId, template.id, { content, baseVersion: base, ...(notes.trim() ? { notes: notes.trim() } : {}) }),
    onSuccess: (next) => {
      refresh(next);
      setBase(next.currentVersion);
      setNotes('');
      setNotice(`Saved as version ${next.currentVersion}.${next.warnings.length ? ` Warnings: ${next.warnings.map((w) => w.message).join('; ')}.` : ''}`);
    },
  });
  const loadVersion = useMutation({
    mutationFn: (version: number) => api.getTemplateVersion(environmentId, template.id, version),
    onSuccess: (v) => {
      setContent(v.content);
      setEditorKey((k) => k + 1);
      setNotice(`Version ${v.version} loaded into the editor: save it to make it the current version.`);
    },
  });
  const duplicate = useMutation({
    mutationFn: () => api.duplicateTemplate(environmentId, template.id),
    onSuccess: (copy) => void navigate({ to: '/env/$environmentId/content/templates/$templateId', params: { environmentId, templateId: copy.id } }),
  });
  const archive = useMutation({
    mutationFn: () => (template.archivedAt ? api.restoreTemplate(environmentId, template.id) : api.archiveTemplate(environmentId, template.id)),
    onSuccess: (next) => {
      refresh(next);
      void queryClient.invalidateQueries({ queryKey: ['templates', environmentId] });
      const warnings = 'warnings' in next && Array.isArray(next.warnings) ? next.warnings : [];
      setNotice(next.archivedAt ? `Archived: hidden from the template pickers.${warnings.length ? ` Drafts built from it: ${warnings.map((w) => w.name).join(', ')}.` : ''}` : 'Restored.');
    },
  });
  const archiveDependents = archive.error instanceof ApiError && Array.isArray(archive.error.problem.dependents) ? (archive.error.problem.dependents as Array<{ objectId: string; name: string }>) : [];

  return (
    <section className="template-editor">
      <p>
        <Link to="/env/$environmentId/content/templates" params={{ environmentId }}>
          ← Templates
        </Link>
      </p>
      <h1>{template.name}</h1>
      <p className="hint">
        {TEMPLATE_CHANNEL_LABELS[template.channel]} template · current version v{template.currentVersion} · API id <code>{template.apiId}</code>
      </p>
      {!has('content.edit') && <ReadOnlyBanner>You can view templates but not change them.</ReadOnlyBanner>}
      {template.archivedAt && <p className="notice notice--warning">Archived {template.archivedAt.slice(0, 10)}: hidden from the template pickers. Messages built from it keep their copies.</p>}
      {notice && <p role="status">{notice}</p>}
      <fieldset className="stack-form">
        <legend>Details</legend>
        <Field label="Template name">
          <Input value={name} maxLength={200} disabled={!canEdit} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Description">
          <Textarea value={description} rows={2} maxLength={1000} disabled={!canEdit} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        {canEdit && (
          <Button size="sm" variant="secondary" disabled={!name.trim() || saveDetails.isPending} onClick={() => saveDetails.mutate()}>
            Save details
          </Button>
        )}
        {saveDetails.isError && <IssueList error={saveDetails.error} />}
        <TagPicker environmentId={environmentId} objectType="template" objectId={template.id} canEdit={has('content.edit')} />
      </fieldset>
      <section aria-label="Content">
        <h2>Content</h2>
        <TemplateContentEditor key={editorKey} environmentId={environmentId} channel={template.channel} content={content} readOnly={!canEdit} onChange={setContent} />
        {canEdit && (
          <div className="stack-form">
            <Field label="Version notes">
              <Input value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} />
            </Field>
            <Button variant="primary" disabled={saveVersion.isPending} onClick={() => saveVersion.mutate()}>
              Save as new version
            </Button>
          </div>
        )}
        {saveVersion.isError && <IssueList error={saveVersion.error} />}
      </section>
      <section aria-label="Versions">
        <h2>Versions</h2>
        <ol className="template-versions">
          {template.versions.map((v) => (
            <li key={v.version}>
              <strong>v{v.version}</strong> · {v.createdAt.slice(0, 10)}
              {v.createdBy?.email ? ` · ${v.createdBy.email}` : ''}
              {v.notes ? ` · ${v.notes}` : ''}
              {canEdit && v.version !== template.currentVersion && (
                <Button size="sm" variant="ghost" onClick={() => loadVersion.mutate(v.version)}>
                  Load v{v.version} into the editor
                </Button>
              )}
            </li>
          ))}
        </ol>
      </section>
      <section aria-label="Where used">
        <h2>Where used</h2>
        {template.usage.length === 0 ? (
          <p className="hint">No campaign or Canvas has been built from this template yet.</p>
        ) : (
          <ul>
            {template.usage.map((u) => (
              <li key={`${u.objectType}:${u.objectId}`}>
                {u.url ? <a href={u.url}>{u.name}</a> : u.name} · {u.objectType === 'canvas' ? 'Canvas' : 'Campaign'}
                {u.status ? ` · ${u.status}` : ''}
              </li>
            ))}
          </ul>
        )}
      </section>
      {has('content.edit') && (
        <div className="template-actions" role="group" aria-label="Template actions">
          {!template.archivedAt && (
            <Button variant="secondary" disabled={duplicate.isPending} onClick={() => duplicate.mutate()}>
              Duplicate
            </Button>
          )}
          <Button variant={template.archivedAt ? 'secondary' : 'danger'} disabled={archive.isPending} onClick={() => archive.mutate()}>
            {template.archivedAt ? 'Restore' : 'Archive'}
          </Button>
        </div>
      )}
      {archive.isError && (
        <div role="alert" className="bb-field__error">
          <p>{errorText(archive.error)}</p>
          {archiveDependents.length > 0 && <p>Stop or finish these first: {archiveDependents.map((d) => d.name).join(', ')}.</p>}
        </div>
      )}
      {duplicate.isError && <IssueList error={duplicate.error} />}
    </section>
  );
}

/** Content → Templates → one template (BB-56): details and tags, the channel composer, versions, where-used, duplicate and archive. */
export function TemplateEditor({ environmentId, templateId }: { environmentId: string; templateId: string }) {
  const api = useApi();
  const template = useQuery({ queryKey: ['template', environmentId, templateId], queryFn: () => api.getTemplate(environmentId, templateId) });
  if (template.isPending) return <p aria-busy="true">Loading template…</p>;
  if (template.isError) return <ErrorState title="The template could not be loaded" message={errorText(template.error)} onRetry={() => void template.refetch()} />;
  return <EditorBody key={template.data.id} environmentId={environmentId} template={template.data} />;
}
